import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@strivelabs/ui-core/v2';
import {
  useDeleteWorkflowExecution,
  useUpdateWorkflowExecution,
  type WorkflowExecution,
} from '@/hooks/useWorkflowExecutions';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';

/** Matches the server-side maxLength on the PATCH schema. */
export const CHAT_TITLE_MAX_LENGTH = 120;

/**
 * Rename and delete for a conversation, including the dialog state each needs.
 *
 * Kept apart from `useChatList` because reading the list and mutating a row are
 * independent concerns — the row components take only what they use.
 */
export function useChatActions(workflowInstanceId: string) {
  const navigate = useNavigate();
  const trackEvent = useTrack();
  const { executionId } = useParams<{ executionId?: string }>();

  const [renaming, setRenaming] = useState<WorkflowExecution | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { mutateAsync: updateExecution, isPending: isRenaming } =
    useUpdateWorkflowExecution(workflowInstanceId);
  const { mutateAsync: deleteExecution, isPending: isDeleting } =
    useDeleteWorkflowExecution(workflowInstanceId);

  const rename = useCallback(
    async (title: string) => {
      // Guards a double-fire from Enter-then-click while the request is in
      // flight; the mutation would reject the second call anyway, but returning
      // early avoids the wasted hop.
      if (!renaming || isRenaming) return;

      const trimmed = title.trim();
      if (trimmed === (renaming.title ?? '')) {
        setRenaming(null);
        return;
      }

      try {
        await updateExecution({
          workflowExecutionId: String(renaming.id),
          // Empty clears the title back to the derived fallback.
          title: trimmed || null,
        });
        trackEvent(ANALYTICS_EVENTS.CHAT_THREAD_RENAMED, {});
        toast.success('Chat renamed');
        setRenaming(null);
      } catch {
        toast.error('Failed to rename chat');
      }
    },
    [renaming, isRenaming, updateExecution, trackEvent],
  );

  const confirmDelete = useCallback(async () => {
    if (!deletingId) return;
    try {
      await deleteExecution(deletingId);
      trackEvent(ANALYTICS_EVENTS.CHAT_THREAD_DELETED, {});
      // Confirmed explicitly, as rename is. The row vanishing is not on its own
      // evidence the server accepted anything, and this one is unrecoverable.
      toast.success('Chat deleted');
      // Leaving the conversation you are looking at would strand the route.
      if (deletingId === executionId) navigate('/ui');
      setDeletingId(null);
    } catch {
      toast.error('Failed to delete chat');
    }
  }, [deletingId, deleteExecution, executionId, navigate, trackEvent]);

  return {
    renaming,
    startRename: setRenaming,
    cancelRename: () => setRenaming(null),
    rename,
    isRenaming,
    deletingId,
    startDelete: setDeletingId,
    cancelDelete: () => setDeletingId(null),
    confirmDelete,
    isDeleting,
  };
}
